import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IssueReport } from '../../api/types';
import Permission from '../../common/permissions';
import Card from '../../components/Card';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu';
import { makePermissionListSelector } from '../../selector/authenticationSelector';
import { makePermittedIssueReportListSelector } from '../../selector/issueReportsSelector';
import EditIssueReportDialog from '../issueReportDetails/EditIssueReportDialog';
import Command from '../../action/command';
import IssueReportStatusChip from './IssueReportStatusChip';
import moment from 'moment';
import Logger from '../../common/logger';
import { getIssueTypeText } from './util/issueReportHelpers';
import { useArchiveIssueReport, useRestoreIssueReport } from '../../flows/issueReportArchiveFlows';

interface IssueReportCardProps {
    issueReport: IssueReport;
    cardInfo: {
        title: string;
        subTitle?: string;
        description: string;
    };
    onPress: () => void;
    onChanged?: () => void;
}

const IssueReportCard = (props: IssueReportCardProps) => {
    Logger('IssueReportCard').log(props.issueReport);
    const { t } = useTranslation();
    const [editIssueReportDialogOpen, setEditIssueReportDialogOpen] = useState<boolean>(false);
    const permission = useSelector(makePermissionListSelector());
    const issueReportId = props.issueReport.id || '';
    const permittedIssueReports = useSelector(makePermittedIssueReportListSelector());
    const dispatch = useDispatch();
    const archiveIssueReport = useArchiveIssueReport();
    const restoreIssueReport = useRestoreIssueReport();

    const issueReport = permittedIssueReports.find((issueReport) => issueReport.id === issueReportId);

    // useEffect(() => {
    //     /* eslint-disable no-console */
    //     console.log('IssueReportCard - issueReport', issueReport?.bookingNumber, issueReport);
    //     if (issueReport && issueReport.bookingNumber) {
    //         dispatch(fetchBookingDetails({ bookingId: issueReport.bookingNumber }));
    //     }
    // }, [dispatch, issueReport]);

    if (!issueReport) {
        return null;
    }

    const canEditIssueReports = permission.includes(Permission.editIssueReports());
    const canArchiveIssueReports = permission.includes(Permission.archiveIssueReports());

    const handleIssueReportUpdated = () => {
        dispatch(Command.IssueReport.fetchIssueReports({}));
    };

    const handleArchive = async () => {
        const ok = await archiveIssueReport(issueReport.id);
        if (ok) props.onChanged?.();
    };

    const handleRestore = async () => {
        const ok = await restoreIssueReport(issueReport.id);
        if (ok) props.onChanged?.();
    };

    const actionItems: DropdownMenuItem[] = [
        {
            id: 'archive',
            label: t('issue_reports.archive.button'),
            onClick: handleArchive,
            hidden: !!issueReport.archivedAt,
        },
        {
            id: 'restore',
            label: t('issue_reports.archive.restore'),
            onClick: handleRestore,
            hidden: !issueReport.archivedAt,
        },
    ];
    Logger('IssueReportCard').log('Issue Report Card Rendered', issueReport);
    const cardBaseClass = 'flex flex-col gap-0 p-4 bg-white rounded-lg border border-gray-200 shadow-sm w-full relative min-h-44';
    return (
        <div className={'relative'}>
            <Card onClick={props.onPress} className={`${cardBaseClass} ${issueReport.archivedAt ? 'opacity-60' : ''}`}>
                {/* flex-wrap: the badges and the status chip are all
                    `whitespace-nowrap`, so on a narrow card they squeeze the
                    title out entirely — a report carrying both an archive and
                    a customer-status badge rendered its title as a single
                    letter. Wrapping lets them drop to a second line and keeps
                    the title readable; cards without badges still fit on one
                    line and are unchanged. */}
                <header className={'flex flex-row flex-wrap gap-2 w-full items-center'}>
                    <h2 className={'flex grow truncate text-base font-semibold tracking-[0.02rem] text-gray-900 text-left'}>
                        {(() => {
                            const title = props?.cardInfo?.title?.trim();
                            return title && !title.includes('undefined') ? title : t('issue_reports.general_issue_report_title');
                        })()}
                    </h2>
                    {issueReport.archivedAt && (
                        <span className={'inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500 whitespace-nowrap'}>
                            {t('issue_reports.archive.badge')}
                        </span>
                    )}
                    {/* SUP equipment check (#1604): customer-flow lifecycle. The team
                        works `confirmed`, follows up on stale `open` (abandoned
                        mid-flow); `withdrawn` is informational only. */}
                    {issueReport.status && (
                        <span className={'inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500 whitespace-nowrap'}>
                            {t(`issue_reports.customer_status.${issueReport.status}`)}
                        </span>
                    )}
                    <IssueReportStatusChip label={issueReport.state} />
                    {canArchiveIssueReports && (
                        <span onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu
                                trigger={
                                    <IconButton size={'small'} sx={{ color: '#374151' }}>
                                        <MoreVertIcon fontSize={'small'} />
                                    </IconButton>
                                }
                                items={actionItems}
                                menuPosition={'right'}
                            />
                        </span>
                    )}
                </header>
                {issueReport.createdBy === 'admin' ? (
                    <>
                        <p className={'text-base text-gray-500 text-left'}>
                            {issueReport.service && t(`service_type_selector.${issueReport.service}`)} | {getIssueTypeText(issueReport.issueType, t)}
                        </p>
                    </>
                ) : (
                    <p className={'text-base text-gray-500 text-left'}> {t('issue_reports.general_issue_report_title')}</p>
                )}
                <p className={'text-base text-gray-500 text-left'}>
                    {issueReport.createdBy === 'user'
                        ? issueReport.userCreatorEmail || t('common.end_user')
                        : issueReport?.profileInfo?.email || issueReport.userCreatorEmail || issueReport?.profileInfo?.name}
                    {' • '}
                    <span>{issueReport?.createdAt ? moment(issueReport.createdAt).format('ll LT') : ''}</span>
                </p>
                <span className={'truncate text-gray-900 font-normal tracking-[0.02rem] max-w-full leading-5 text-left'}>{props.cardInfo.description}</span>
            </Card>
            {canEditIssueReports && (
                <EditIssueReportDialog
                    issueReport={props.issueReport}
                    open={editIssueReportDialogOpen}
                    onClose={() => setEditIssueReportDialogOpen(false)}
                    onUpdated={handleIssueReportUpdated}
                />
            )}
        </div>
    );
};

export default IssueReportCard;
